import { format } from 'date-fns-tz';
import cloneDeep from 'lodash/cloneDeep';
import {
  CustomFieldDefinition,
  CustomFieldUsage,
  Tenant,
  FieldStatus,
  FileType,
  FieldDataType,
} from '@alucio/aws-beacon-amplify/src/models';
import { SortDir } from 'src/components/DNA/hooks/useSort';
import { Variant, DocumentORM, DocumentVersionORM } from 'src/types/types';
import { SYSTEM_FIELD_CONFIGS } from './constants';

export interface ListFieldConfig {
  id?: string,
  fieldName: string,
  altFieldName?: string,
  fieldLabel: string,
  fieldType: ('LABEL' | 'MODEL' | 'ACTION_MENU' | 'DOC_MODEL'),
  dataType?: ('string' | 'date' | 'boolean')
  width: number,
  defaultSort?: SortDir | keyof typeof SortDir,
  showByDefault?: boolean,
  isBadge?: boolean,
  fieldResolver?: (docVer: DocumentVersionORM) => any
}

// TODO: This is a temporary solution to convert the CustomFieldDefinition['fieldType'] to ListFieldConfig['dataType']
// We should remove this once we have a better solution
// Probably use the saame enumerator that we save on the custom values
const toListConfigDataType = (dataType: CustomFieldDefinition['fieldType']): ListFieldConfig['dataType'] => {
  switch (dataType) {
    case FieldDataType.CATEGORICAL:
      return 'string'
    case FieldDataType.MULTICATEGORICAL:
      return 'string'
    case FieldDataType.DATE:
      return 'date'
    case FieldDataType.USER_LIST:
      return 'string'
    default:
      return 'string'
  }
}

const getFieldConfigForCustomField = (field: CustomFieldDefinition): ListFieldConfig => {
  return {
    id: field.id,
    fieldName: field.fieldName,
    fieldType: 'LABEL',
    fieldLabel: field.fieldLabel,
    width: 240,
    isBadge: field.settings?.isBadge,
    dataType: toListConfigDataType(field.fieldType),
  }
}

export const getFieldConfigsForTenant = (
  tenant: Tenant | undefined,
  variant: Variant,
  isDesktop: boolean,
  selectEnabled: boolean = false,
): ListFieldConfig[] => {
  let fieldConfig: ListFieldConfig[] = [];

  // We lock the first couple of columns to ensure consistent UI
  const systemFields = cloneDeep(SYSTEM_FIELD_CONFIGS) as Record<string, ListFieldConfig>
  if (!isDesktop) {
    systemFields.ACTION_MENU.width = 60;
    systemFields.THUMBNAIL.width = 140;
  }
  if (variant === Variant.MSL || variant === Variant.bookmark) {
    const actionMenu = {
      ...systemFields.ACTION_MENU,
      width: !isDesktop
        ? selectEnabled ? 140 : 90
        : selectEnabled ? 100 : 70,
    };

    fieldConfig.push(actionMenu);
    fieldConfig.push(systemFields.THUMBNAIL);
    fieldConfig.push(systemFields.TITLE);
  } else {
    fieldConfig.push(systemFields.ACTION_MENU);
    fieldConfig.push(systemFields.THUMBNAIL);
    fieldConfig.push(systemFields.LABEL_STATUS_DOT);
    fieldConfig.push(systemFields.TITLE);
    fieldConfig.push(systemFields.STATUS);
  }

  const customConfig =
    variant === Variant.MSL || variant === Variant.bookmark
      ? tenant?.config?.mslListConfig
      : tenant?.config?.publisherListConfig;
  const hasConfiguredFields = !!customConfig?.fields?.length;

  if (hasConfiguredFields && (variant === Variant.MSL || variant === Variant.publisher)) {
    const customFields = (customConfig.fields ?? []).map((field) => {
      let config: ListFieldConfig;
      if (systemFields[field.field]) {
        if (variant === Variant.publisher && field.field === 'DOC_UPDATED_AT') {
          config = systemFields.PUBLISHER_DOC_UPDATED_AT;
        }
        else {
          config = systemFields[field.field];
        }
      }
      else {
        const tenantFieldConfig = tenant?.config?.customFields?.find(
          (label) => label.id === field.field || label.fieldName === field.field,
        );
        if (!tenantFieldConfig) {
          throw new Error(`Invalid tenant list configuration field \
        ${field.field} not found in tenant's field configuration`);
        } else if (tenantFieldConfig.status === FieldStatus.DISABLED) {
          throw new Error(`Invalid tenant list configuration field \
        ${field.field} (${tenantFieldConfig.fieldLabel}) is disabled`);
        }
        config = getFieldConfigForCustomField(tenantFieldConfig);
      }
      config.width = field.width ? field.width : config.width;
      if (config.width < 150) {
        config.width = 150;
      }
      return config;
    });
    fieldConfig = [...fieldConfig, ...customFields];
    if (customConfig.sort) {
      const sortFieldName = systemFields[customConfig.sort.field]
        ? systemFields[customConfig.sort.field].fieldName
        : customConfig.sort.field;
      const sortField = fieldConfig.find(
        (field) => field.fieldName === sortFieldName || field.id === sortFieldName,
      );
      if (!sortField) {
        throw new Error(`Invalid tenant list configuration sort field \
        ${customConfig.sort.field} not found in available list fields`);
      }
      sortField.defaultSort = customConfig.sort.isAsc
        ? SortDir.asc
        : SortDir.desc;
    }
  }
  else {
    const defaultTenantFields = (
      tenant?.config?.customFields?.filter((field) => (
        !!field.showByDefault &&
        !field.settings?.isBadge && field.usage.includes(CustomFieldUsage.DOCUMENT) &&
        field.status === FieldStatus.ENABLED
      )) ?? []).map(getFieldConfigForCustomField);
    const badgeField = tenant?.config?.customFields?.find((field) => field.settings?.isBadge &&
      field.usage.includes(CustomFieldUsage.DOCUMENT) && field.status === FieldStatus.ENABLED);
    if (badgeField) {
      defaultTenantFields.unshift(getFieldConfigForCustomField(badgeField));
    }

    if (variant === Variant.MSL) {
      fieldConfig = [...fieldConfig, ...defaultTenantFields];
      fieldConfig.push({
        ...systemFields.DOC_UPDATED_AT,
        defaultSort: SortDir.desc,
      });
    }
    else if (variant === Variant.bookmark) {
      fieldConfig = [...fieldConfig, ...defaultTenantFields];

      const titleField = fieldConfig.find((field) => field.fieldName === systemFields.TITLE.fieldName);
      if (titleField) {
        titleField.defaultSort = SortDir.asc;
      }
    }
    else {
      fieldConfig = [...fieldConfig, ...defaultTenantFields];
      fieldConfig.push({
        ...systemFields.PUBLISHER_DOC_UPDATED_AT,
        defaultSort: SortDir.desc,
      });
    }
  }
  return fieldConfig;
};

export const getFormattedModelValue = (field: ListFieldConfig, documentORM: DocumentORM) => {
  const value = field.fieldType === 'MODEL'
    ? (field.fieldResolver
      ? field.fieldResolver(documentORM.relations.version.latestDocumentVersionORM)
      : documentORM.relations.version.latestUsableDocumentVersionORM?.model[field.fieldName])
    : documentORM.model[field.fieldName]

  switch (field.dataType) {
    case 'date':
      return value ? format(new Date(value), 'MM/dd/yyyy') : ''
    case 'boolean':
      return value ? 'Yes' : 'No'
    default:
      if (field.fieldName === 'type' && value === FileType.MP4) return 'VIDEO'
      return value;
  }
}
